import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { LeagueState, MatchWithPlayers } from './league.state';
import {
  errorState,
  HttpRequestState,
  isErrorState,
  isLoadedState,
  isLoadingState,
  loadedState,
  loadingState,
} from 'ngx-http-request-state';
import {
  deleteLeagueResultModalActions,
  divisionDetailsComponentActions,
  editLeagueResultModalActions,
  leagueApiActions,
} from './league.actions';
import { playersFeature } from '@pool-league/frontend-web-data-players';

const INITIAL_STATE: LeagueState = {
  divisions: loadedState([]),
  divisionMatches: loadedState([]),
  deleteResultRequestState: undefined,
  saveResultRequestState: undefined,
  focusedDivision: undefined,
};

export const leagueFeature = createFeature({
  name: 'league',
  reducer: createReducer(
    INITIAL_STATE,
    on(
      divisionDetailsComponentActions.playoffUpdated,
      (state, { playoff }) => ({
        ...state,
        divisions: isLoadedState(state.divisions)
          ? {
              ...state.divisions,
              value: state.divisions.value.map((division) =>
                division.divisionId === playoff.divisionId
                  ? {
                      ...division,
                      playoff,
                    }
                  : division,
              ),
            }
          : state.divisions,
      }),
    ),
    on(
      divisionDetailsComponentActions.loadDivision,
      (state, { seasonId, format, divisionId }) => ({
        ...state,
        focusedDivision: {
          seasonId,
          format,
          divisionId,
        },
      }),
    ),
    on(divisionDetailsComponentActions.unloadDivision, (state) => ({
      ...state,
      divisionMatches: loadedState([]),
      deleteResultRequestState: undefined,
      focusedDivision: undefined,
    })),
    on(leagueApiActions.divisionsLoading, (state) => ({
      ...state,
      divisions: loadingState(),
    })),
    on(
      leagueApiActions.divisionsLoadSuccess,
      leagueApiActions.leagueCreated,
      (state, { divisions }) => ({
        ...state,
        divisions: loadedState(divisions),
      }),
    ),
    on(leagueApiActions.divisionsLoadError, (state, { error }) => ({
      ...state,
      divisions: errorState(error),
    })),
    on(
      leagueApiActions.divisionLoadSuccess,
      leagueApiActions.deleteResultSuccess,
      leagueApiActions.saveResultSuccess,
      (state, { division }) => ({
        ...state,
        divisions: isLoadedState(state.divisions)
          ? loadedState(
              state.divisions.value.map((d) =>
                d.divisionId === division.divisionId ? division : d,
              ),
            )
          : state.divisions,
        divisionMatches: loadedState(division.matches),
        deleteResultRequestState: undefined,
      }),
    ),
    on(leagueApiActions.divisionLoading, (state) => ({
      ...state,
      divisionMatches: loadingState(),
    })),
    on(leagueApiActions.divisionLoadError, (state, { error }) => ({
      ...state,
      divisionMatches: errorState(error),
    })),
    on(leagueApiActions.deleteResultRequestSent, (state) => ({
      ...state,
      deleteResultRequestState: loadingState(),
    })),
    on(leagueApiActions.deleteResultError, (state, { error }) => ({
      ...state,
      deleteResultRequestState: errorState(error),
    })),
    on(deleteLeagueResultModalActions.modalClosed, (state) => ({
      ...state,
      deleteResultRequestState: undefined,
    })),
    on(editLeagueResultModalActions.modalClosed, (state) => ({
      ...state,
      saveResultRequestState: undefined,
    })),
    on(leagueApiActions.saveResultRequestSent, (state) => ({
      ...state,
      saveResultRequestState: loadingState(),
    })),
    on(leagueApiActions.saveResultError, (state, { error }) => ({
      ...state,
      saveResultRequestState: errorState(error),
    })),
  ),
  extraSelectors: ({ selectDivisionMatches }) => ({
    selectDivisionMatchesWithPlayers: createSelector(
      selectDivisionMatches,
      playersFeature.selectEntities,
      (matches, players) => {
        if (isLoadingState(matches) || isErrorState(matches)) {
          return matches as HttpRequestState<MatchWithPlayers[]>;
        }
        return loadedState<MatchWithPlayers[]>(
          matches.value.map((match) => ({
            ...match,
            breaker: players[match.breaker] || {
              id: match.breaker,
              displayName: match.breaker,
              isMe: false,
              retired: false,
            },
            opponent: players[match.opponent] || {
              id: match.opponent,
              displayName: match.opponent,
              isMe: false,
              retired: false,
            },
          })),
        );
      },
    ),
  }),
});
